/* eslint-disable */

import type { Address } from "viem"
import type { Config } from "wagmi"
import { isAddress } from "viem"

import type { EmitEvent, TokenAddress, TokenSymbol } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { EventEmitter } from "@/domains/library"
import { ManualClPoolCreatedEvent } from "@/domains/thick-v2/events"
import { createThickV2NfpmPool } from "@/domains/on-chain/transactions/thick-v2/create-nfpm-pool"

interface CreateNfpmPoolInteractionParams {
  account: Address
  chainId: number
  config: Config
  token0: TokenAddress & TokenSymbol
  token1: TokenAddress & TokenSymbol
  tickSpacing: number
  sqrtPriceX96: bigint
  nfpManagerAddress: Address
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
}

export const createNfpmPoolInteraction = async ({
  account,
  chainId,
  config,
  token0,
  token1,
  tickSpacing,
  sqrtPriceX96,
  nfpManagerAddress,
  emitter,
  eventBus,
}: CreateNfpmPoolInteractionParams) => {
  emitter.emit("creating-nfpm-pool", {
    title: "Create Thick T1 CL pool",
    description: `Creating Thick T1 CL pool for ${token0.symbol} and ${token1.symbol}`,
    variant: "info",
  })

  const result = await createThickV2NfpmPool({
    account,
    chainId,
    config,
    token0Address: token0.address,
    token1Address: token1.address,
    tickSpacing,
    sqrtPriceX96,
    nfpManagerAddress,
  })

  if (result.success) {
    /*
     * the pair address is in the outputs, lets see if we can find it..
     * outputs: [{ internalType: "address", name: "pool", type: "address" }],
     */
    // const poolAddress = result.data.outputs[0]

    // Extract pool address from transaction if available
    // For now, we'll publish the event with transaction hash
    // The orchestrator will refresh the pool list to pick up the new pool
    const poolAddress = extractCreatedPoolAddress(result.simulatedResult)
    const transactionHash = result.tx

    if (poolAddress || transactionHash) {
      eventBus.publish(
        new ManualClPoolCreatedEvent({
          poolAddress,
          transactionHash,
        }),
      )
    }

    emitter.emit("create-nfpm-pool", {
      title: "Create Thick T1 CL pool",
      description: `Nfpm pool created for ${token0.symbol} and ${token1.symbol}`,
      variant: "success",
    })
  }

  if (result.success === false) {
    emitter.emit("error-creating-nfpm-pool", {
      title: "Create Thick T1 CL pool failed",
      description: result.error,
      variant: "error",
    })
  }

  return result
}

export function extractCreatedPoolAddress(simulatedResult: unknown): Address | undefined {
  const output = Array.isArray(simulatedResult) ? simulatedResult[0] : simulatedResult
  return typeof output === "string" && isAddress(output) ? output : undefined
}
